import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { DismantlingCoefficientTypeEnum, PriceListUerDismantlingRatioData } from 'gql/graphql';
import { Button, IconArrowDown, Text, IconArrowRight, Checkbox } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import type { IrowGroupTitleItem } from '../components/UerSelectionModal';
import {
    uerPriceListUerForDismantlingRatioUerCodeFilter,
    uerPriceListUerForDismantlingRatioUerNameFilter,
    uerPriceListUerForDismantlingRatioUerUnitFilter,
} from '../components/UerSelectionModalQueries';

interface IUseUerRatiosColumns {
    itemsData?: Partial<PriceListUerDismantlingRatioData>[] | null;
    checkedIds?: number[];
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
    groupType: DismantlingCoefficientTypeEnum;
    contractId: number;
    uerId: number;
    groupStates: Record<string, boolean>;
    setGroupStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    onCheckChange: (checkedIds: number[]) => void;
}

export const UerRatiosColumns = (props: IUseUerRatiosColumns): ColDef[] => {
    const {
        checkedIds,
        setFilterValues,
        groupType,
        contractId,
        uerId,
        groupStates,
        setGroupStates,
        onCheckChange,
        itemsData,
    } = props;

    const isRowChecked = useCallback((rowId: number) => checkedIds?.includes(rowId), [checkedIds]);

    const toggleRow = useCallback(
        (rowId: number) => {
            const currentCheckedIds = checkedIds ?? [];

            const updateCheckedIds = currentCheckedIds.includes(rowId)
                ? currentCheckedIds.filter((id) => id !== rowId)
                : [...currentCheckedIds, rowId];

            onCheckChange(updateCheckedIds);
        },
        [checkedIds, onCheckChange],
    );

    const isGroupChecked = useCallback(
        (ratioCode: string | undefined, allRows: any[]) => {
            if (!ratioCode || checkedIds?.length === 0) {
                return false;
            }

            return allRows.every((row) => checkedIds?.includes(row.uerId));
        },
        [checkedIds],
    );

    const toggleGroupChecked = useCallback(
        (groupRows: Partial<PriceListUerDismantlingRatioData>[]) => {
            const groupIds = groupRows.map((row) => row.uerId);
            const isGroupSelected = groupIds.every((id) => checkedIds?.includes(id));

            const updateCheckedIds = isGroupSelected
                ? checkedIds?.filter((id) => !groupIds.includes(id)) ?? []
                : [...(checkedIds ?? []), ...groupIds.filter((id) => !checkedIds?.includes(id))];

            onCheckChange(updateCheckedIds);
        },
        [checkedIds, onCheckChange],
    );

    const toggleGroup = useCallback(
        (ratioCode) => {
            setGroupStates((prev) => ({ ...prev, [ratioCode]: !prev[ratioCode] }));
        },
        [setGroupStates],
    );

    const filterVariables = useMemo(
        () => ({
            groupType,
            contractId,
            preselectedUerBaseRowIds: [uerId],
        }),
        [contractId, groupType, uerId],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    return useMemo(
        () => [
            {
                field: 'checkbox' as any,
                headerName: '',
                width: 55,
                flex: 0,
                cellRenderer: ({ data }: { data: any }) => {
                    const isChecked = data.uerId ? isRowChecked(data.uerId) : false;
                    const isGroupRow = !!data?.isGroup;
                    const groupRows = (itemsData || [])?.filter((row) => row.ratioCode === data.ratioCode);

                    return (
                        <Checkbox
                            checked={isGroupRow ? isGroupChecked(data.ratioCode, groupRows) : isChecked}
                            onChange={() => (isGroupRow ? toggleGroupChecked(groupRows) : toggleRow(data.uerId))}
                            disabled={!isGroupRow && isGroupChecked(data.ratioCode, groupRows)}
                        />
                    );
                },
            },
            {
                headerName: 'Коэффициент / Подраздел',
                field: 'groupName',
                width: 450,
                cellRenderer: ({ data }: { data: IrowGroupTitleItem | PriceListUerDismantlingRatioData }) => {
                    if ('isGroup' in data && data?.isGroup === true) {
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                                <Button
                                    size="s"
                                    view="clear"
                                    label="button"
                                    onlyIcon
                                    iconRight={groupStates[data.ratioCode] ? IconArrowRight : IconArrowDown}
                                    onClick={() => toggleGroup(data.ratioCode)}
                                />
                                <Text view="primary" lineHeight="l" size="m" weight="medium" transform="uppercase">
                                    {data.ratioCode} ({data.rowCount})
                                </Text>
                            </div>
                        );
                    }

                    return (
                        <div style={{ paddingLeft: '40px' }}>{`${
                            (data as PriceListUerDismantlingRatioData).uerGroupCode
                        } ${(data as PriceListUerDismantlingRatioData).uerGroupName}`}</div>
                    );
                },
            },
            {
                headerName: 'Шифр',
                field: 'uerCode',
                width: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string | null }) => value ?? 'Без шифра',
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerCodeFilter,
                },
            },
            {
                headerName: 'Наименование',
                field: 'uerName',
                width: 450,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerNameFilter,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: 'unit',
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerUnitFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerUnitFilter,
                },
            },
        ],
        [
            changeFilterHandler,
            filterVariables,
            groupStates,
            isGroupChecked,
            isRowChecked,
            itemsData,
            toggleGroup,
            toggleGroupChecked,
            toggleRow,
        ],
    );
};
